// BlueprintAssetSection — the inspector for one graph in a character's
// blueprint library (ENG-1030).
//
// A row IS one graph — body, face or events — so the primary action is the same
// one the outliner offers on its Body/Face/Event graph rows: OPEN THE EDITOR.
// There is no "Load": that word named an internal step (make this the document
// the editor edits, so Save writes here) which nobody should have to know about.
// Opening does both — it makes this graph the one being edited and shows it.
//
// "Set as default" is the separate, durable act: it changes which graph of this
// kind loads WITH THE CHARACTER, for everyone. The inspector names both states
// so the two are never read as one.

import React, { useState } from 'react'
import { CustomAssetManager } from '../../lib/CustomAssetManager'
import { dialog } from '../../lib/dialog'
import {
  loadBlueprintAssetGraph, blueprintCachesAreForeign, getActiveBlueprintId,
} from '../../lib/loadBlueprint'
// resolveCharacterId, not getCurrentCharacterId(list): this is the SAME
// function loadBlueprint.js uses to build the graph-cache storage keys, so the
// character this section acts on and the caches it acts through cannot disagree.
import { resolveCharacterId } from '../../lib/characterState'
import { blueprintGraphLabel, openGraphSurface } from '../../lib/assetKindColors'
import { downloadBlueprintCard } from '../../lib/blueprintAssetTransfer'
import { withMinVisible } from '../../lib/minVisibleDuration'
import { menubarCommands } from '../../lib/menubarCommands'

// Chrome and tone in palette tokens (--m-online / --m-warn / --m-danger), so
// this panel follows the user's palette like every other surface.
const C = {
  fg:    'var(--m-fg)',
  dim:   'var(--m-fg-2)',
  line:  'var(--m-line-hi)',
  panel: 'var(--m-panel)',
  ok:    'var(--m-online)',
  warn:  'var(--m-warn)',
  err:   'var(--m-danger)',
}

const btn = {
  flex: 1,
  background: 'var(--m-panel-hi)',
  color: C.fg,
  border: `1px solid ${C.line}`,
  borderRadius: 4,
  padding: '5px 8px',
  fontFamily: 'var(--m-font)',
  fontSize: 10.5,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
}

// The open button is the primary action, styled like the outliner's own
// "Open X graph editor" — the accent, not a success tone.
const openBtn = {
  ...btn,
  background: 'color-mix(in srgb, var(--m-accent) 18%, var(--m-panel-hi))',
  border: '1px solid color-mix(in srgb, var(--m-accent) 45%, transparent)',
  color: 'var(--m-accent)',
  fontSize: 11,
  padding: '7px 8px',
}

export default function BlueprintAssetSection({ asset, Section, Field, ReadVal }) {
  const [busy, setBusy] = useState(null)     // 'open' | 'default' | 'duplicate' | 'export' | null
  const [status, setStatus] = useState(null) // { tone: 'ok'|'err', text } | null

  const characterId = resolveCharacterId()
  const graphKind = asset.graphKind
  const label = blueprintGraphLabel(graphKind)
  const isActive = getActiveBlueprintId(characterId, graphKind) === asset.rawId

  const report = (tone, text) => setStatus({ tone, text })

  // Open = hydrate this graph into the editor, then show its surface. Both
  // halves, because either alone is a half-answer: hydrating without opening
  // leaves the change invisible, and opening without hydrating shows the graph
  // that was already there.
  const open = async () => {
    if (!graphKind) {
      report('err', 'This row does not say which graph it is, so there is no editor to open.')
      return
    }
    if (blueprintCachesAreForeign(asset.rawId, characterId, graphKind)) {
      const ok = await dialog.confirm({
        title: `Open "${asset.name}"?`,
        body: `The editor currently holds a different ${label.toLowerCase()}. Any unsaved changes to it will be discarded — save it first if you want to keep them.`,
        confirmLabel: 'Discard and open',
        variant: 'warning',
      })
      if (!ok) return
    }
    setStatus(null)
    // Same floor as the card's bounce: a warm hydration finishes in well under
    // a frame or two, which made this button's "…" appear and vanish without
    // ever being seen (minVisibleDuration.js).
    await withMinVisible(async () => {
      try {
        const res = await loadBlueprintAssetGraph(asset.rawId, characterId, graphKind)
        if (res == null) {
          report('err', `This ${label.toLowerCase()} has no nodes — refusing to load it over the current one.`)
          return
        }
        if (res.superseded) {
          report('err', 'Another graph finished loading first — nothing was changed.')
          return
        }
        // The same command the outliner row and the tool rail run — and it only
        // exists while the Blueprint editor is mounted, so an unavailable surface
        // is reported rather than looking like a dead button.
        if (!openGraphSurface(graphKind, menubarCommands)) {
          report('ok', `Loaded into the ${label.toLowerCase()}. Open the Blueprint editor to see it.`)
        }
      } catch (e) {
        report('err', e?.message ?? String(e))
      }
    }, (on) => setBusy(on ? 'open' : null))
  }

  const setDefault = async () => {
    setBusy('default'); setStatus(null)
    try {
      await CustomAssetManager.setDefaultBlueprintAsset(asset.rawId)
      report('ok', `"${asset.name}" is now the ${label.toLowerCase()} this character loads.`)
    } catch (e) {
      report('err', e?.message ?? String(e))
    } finally {
      setBusy(null)
    }
  }

  const exportToFile = async () => {
    setBusy('export'); setStatus(null)
    try {
      const { fileName } = await downloadBlueprintCard(asset, { character: resolveCharacterId() })
      report('ok', `Exported ${fileName}`)
    } catch (e) {
      // Loud: an export that quietly wrote nothing would only surface on the
      // import side, on another character, later.
      report('err', e?.message ?? String(e))
    } finally {
      setBusy(null)
    }
  }

  const duplicate = async () => {
    setBusy('duplicate'); setStatus(null)
    try {
      // Copy from the SERVER's content, not from the editor's caches — a
      // duplicate must be a copy of the graph on the card, not of whatever
      // happens to be open.
      const full = await CustomAssetManager.getBlueprintAssetContent(asset.rawId)
      const row = await CustomAssetManager.createBlueprintAsset({
        name: `${asset.name} copy`,
        kind: graphKind,
        folderPath: asset.serverFolderPath ?? '/blueprints',
        graph: full.graph,
      })
      report('ok', `Created "${row.name}".`)
    } catch (e) {
      report('err', e?.message ?? String(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <Section title={label}>
      <button onClick={open} disabled={!!busy} style={openBtn}
        title={`Open this ${label.toLowerCase()} in its editor`}>
        {busy === 'open' ? '…' : `Open ${label} editor`}
      </button>

      <div style={{ display: 'flex', gap: 6, margin: '8px 0 10px' }}>
        <button onClick={setDefault} disabled={!!busy || asset.isDefault} style={btn}
          title={asset.isDefault
            ? `This is already the ${label.toLowerCase()} the character loads`
            : `Make this the ${label.toLowerCase()} the character loads`}>
          {busy === 'default' ? '…' : asset.isDefault ? '★ Default' : '☆ Set as default'}
        </button>
        <button onClick={duplicate} disabled={!!busy} style={btn}
          title={`Create a copy of this ${label.toLowerCase()}`}>
          {busy === 'duplicate' ? '…' : '⧉ Duplicate'}
        </button>
        <button onClick={exportToFile} disabled={!!busy} style={btn}
          title={`Export this ${label.toLowerCase()} to a file — re-imports onto any character as a NEW card`}>
          {busy === 'export' ? '…' : '↓ Export…'}
        </button>
      </div>

      {status && (
        <div style={{
          fontFamily: 'var(--m-font)', fontSize: 10, lineHeight: 1.45,
          color: status.tone === 'ok' ? C.ok : C.err,
          background: C.panel,
          border: `1px solid ${status.tone === 'ok'
            ? 'color-mix(in srgb, var(--m-online) 33%, transparent)'
            : 'color-mix(in srgb, var(--m-danger) 33%, transparent)'}`,
          borderRadius: 4, padding: '5px 7px', marginBottom: 10,
        }}>
          {status.text}
        </div>
      )}

      <Field label="Graph"><ReadVal value={label} mono /></Field>
      <Field label="Loads with character">
        <span style={{
          fontFamily: 'var(--m-font)', fontSize: 11,
          color: asset.isDefault ? C.ok : C.dim,
        }}>
          {asset.isDefault ? 'yes — this is the default' : 'no'}
        </span>
      </Field>
      {/* "In the editor now" is session state, distinct from the default above.
          Naming both prevents the reading that opening a graph changed what the
          character loads for everyone. */}
      <Field label="In the editor now">
        <span style={{
          fontFamily: 'var(--m-font)', fontSize: 11,
          color: isActive ? C.warn : C.dim,
        }}>
          {isActive ? 'yes — edits here save to this graph' : 'no'}
        </span>
      </Field>
      <Field label="Version"><ReadVal value={asset.version ?? '—'} mono /></Field>
      <Field label="Updated"><ReadVal value={fmtWhen(asset.updatedAt)} mono small /></Field>
      {asset.createdBy && <Field label="Created by"><ReadVal value={asset.createdBy} mono small /></Field>}
    </Section>
  )
}

function fmtWhen(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString()
}
